import React, { useState, useEffect, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, FormControlLabel, FormHelperText, Switch, Typography } from '@mui/material';
import SweetAlert from '../../../Shared/Notification';
import { ConstantActions, FlowBuilderActions } from '../../../../Services/_actions';
import { botFlowDataAtom, flowObjAtom, flowValidationAtom } from '../../../../Services/_states';
import { useRecoilValue } from 'recoil';
import { useSnackBar } from '../../Common/SnackBar/index.tsx';
import LoadingWrapper from '../../../Shared/LoadingWrapper/index.jsx';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import RenderTextArea from './RenderTextArea.tsx';
import FileInput from '../../../Shared/FileUploadPreview/FileInput.jsx';
import {
  BLOCKED_EXTENSIONS,
  WHATSAPP_ALLOWED_MIME,
  WHATSAPP_LIMITS,
} from '../../../Whatsapp/Template/template.constant.js';
import SearchableDropdown from '../../Common/SearchableDropdown/SearchableDropdown.tsx';
import { getAllParentNodeResults } from '../../helper.tsx';

interface SuggestionObj {
  label: string;
  value: string;
  paramType?: string;
}

const ImageNode = ({ data }: any) => {
  const constantActions = ConstantActions();
  const actions = FlowBuilderActions();
  const [isLoading, setIsLoading] = useState(false);
  const flowValidationObj = useRecoilValue(flowValidationAtom);
  const botFlowData: any = useRecoilValue(botFlowDataAtom);
  const { showSnackBar } = useSnackBar();
  const [bodyText, setBodyText] = useState(data?.body || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>(['Option 1', 'Option 2', 'Option 3']);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const flowObj = useRecoilValue(flowObjAtom);

  const parentResults = getAllParentNodeResults(data?.id ?? null, flowObj);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (data.body !== bodyText) {
      setBodyText(data.body || '');
    }
  }, [data.body]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 12}px`;
    }
  }, [bodyText]);

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setBodyText(value);

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);

    // Detect `{{@` pattern
    const match = textBeforeCursor.match(/{{@(\w*)$/);
    if (match) {
      // const query = match[1]; // Get the text after `{{@`
      // setFilteredSuggestions(suggestions.filter((s) =>s.label.toLowerCase().includes(query.toLowerCase())));
      setShowSuggestions(true);
      setCursorPosition(cursorPos);
    } else {
      setShowSuggestions(false);
    }

    // Update `params` by finding all valid `{{label}}` entries in the text
    const matches = value.match(/{{(.*?)}}/g); // Match all `{{label}}`
    const labelsInBody = matches ? matches.map((match) => match.replace(/{{|}}/g, '')) : [];

    // Categorize params by `paramType`
    const categorizedParams: any = {
      global: [],
      result: [],
      webform: [],
    };

    data.parentResults.forEach((result: SuggestionObj) => {
      if (labelsInBody.includes(result.label)) {
        if (result.paramType === 'global') {
          categorizedParams.global.push(result.value);
        } else if (result.paramType === 'result') {
          categorizedParams.result.push(result.value);
        } else if (result.paramType === 'webform') {
          categorizedParams.webform.push(result.value);
        }
      }
    });

    // Update parent data
    data.onChange(data.id, { body: value, params: categorizedParams });

    // Adjust the textarea height to fit content
    // if (textareaRef.current) {
    //   textareaRef.current.style.height = "auto"; // Reset height
    //   textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 12
    //     }px`; // Set height to content
    // }
  };

  const handleSuggestionClick = (suggestion: SuggestionObj) => {
    if (cursorPosition !== null) {
      const textBeforeCursor = bodyText.slice(0, cursorPosition);
      const textAfterCursor = bodyText.slice(cursorPosition);
      const updatedText =
        textBeforeCursor.replace(/{{@\w*$/, `{{${suggestion.label}}}`) + textAfterCursor;

      setBodyText(updatedText);
      setShowSuggestions(false);

      // Initialize parameters if not present
      const updatedParams = {
        global: data.params?.global || [],
        result: data.params?.result || [],
        webform: data.params?.webform || [],
      };

      // Add the value to the correct array based on paramType
      if (suggestion.paramType === 'global') {
        updatedParams.global = [...updatedParams.global, suggestion.value];
      } else if (suggestion.paramType === 'result') {
        updatedParams.result = [...updatedParams.result, suggestion.value];
      } else if (suggestion.paramType === 'webform') {
        updatedParams.webform = [...updatedParams.webform, suggestion.value];
      }

      // Update parent data
      data.onChange(data.id, { body: updatedText, params: updatedParams });

      // Move cursor to the end of the inserted suggestion
      // setTimeout(() => {
      //   textareaRef.current?.setSelectionRange(
      //     updatedText.length,
      //     updatedText.length
      //   );
      // }, 0);
    }
  };

  const inferDocType = (mime: string): 'image' | 'video' | 'audio' | 'file' | null => {
    if (WHATSAPP_ALLOWED_MIME.image.includes(mime)) return 'image';
    if (WHATSAPP_ALLOWED_MIME.video.includes(mime)) return 'video';
    if (WHATSAPP_ALLOWED_MIME.audio.includes(mime)) return 'audio';
    if (WHATSAPP_ALLOWED_MIME.file.includes(mime)) return 'file';
    return null;
  };
  const bytesToMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // const file = event?.target?.files?.[0];
    // if (!file) return;

    // const validImageTypes = ['image/jpeg', 'image/png'];
    // const fileExtension = file.name.split('.').pop()?.toLowerCase();
    // const validExtensions = ['jpeg', 'jpg'];

    // if (!validImageTypes.includes(file.type)) {
    //   showSnackBar('Only image files (jpg, png, gif, webp) are allowed.', 'error');
    //   event.target.value = '';
    //   return;
    // }

    // if (fileExtension && !validExtensions.includes(fileExtension)) {
    //   showSnackBar('Invalid file extension. Only image files are allowed.', 'error');
    //   event.target.value = '';
    //   return;
    // }

    // const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
    // if (file.size > MAX_IMAGE_SIZE) {
    //   showSnackBar(`Image size must be less than 10 MB.`, 'error');
    //   event.target.value = '';
    //   return;
    // }
    const file = event?.target?.files?.[0];
    if (!file) return;
    const fileName = file?.name?.toLowerCase();
    const ext = fileName.split('.').pop();
    if (BLOCKED_EXTENSIONS.has(ext)) {
      showSnackBar(`"${ext}" files are not allowed for security reasons.`, 'error');
      event.target.value = '';
      return;
    }
    const typeGroup = inferDocType(file.type);
    if (!typeGroup) {
      showSnackBar(`Unsupported file type (${file.type || ext}).`, 'error');
      event.target.value = '';
      return;
    }

    const limit = WHATSAPP_LIMITS[typeGroup];
    if (file.size > limit) {
      showSnackBar(
        `File too large for ${typeGroup}. Max ${bytesToMB(limit)} MB. Your file is ${bytesToMB(file?.size)} MB.`,
        'error'
      );
      event.target.value = '';
      return;
    }
    setIsLoading(true);
    try {
      let formData = new FormData();
      formData.append('bot_number', botFlowData?.phone_number_id || '');
      formData.append('uploaded_file', file);
      let resp = await actions.uploadMediaToWhatsapp(formData);

      if (resp?.status === 200) {
        let { messaging_product, ...updatedData } = resp?.data;
        data.onChange(data.id, { media: updatedData });
        showSnackBar('Image uploaded successfully!', 'success');
      } else {
        showSnackBar(resp?.response?.data?.detail || 'Error uploading image', 'error');
      }
    } catch (error) {
      showSnackBar('Error uploading image', 'error');
      console.error('Upload error:', error);
    } finally {
      setIsLoading(false);
      event.target.value = '';
    }
  };
  return (
    <div>
      <Box mb={2}>
        <FormControlLabel
          control={
            <Switch
              checked={data.ai_mode_enabled ?? false}
              onChange={(e) => {
                const enabled = e.target.checked;

                if (!enabled) {
                  data.onChange(data.id, { ai_mode_enabled: false, ai_query_parameters: [] });
                } else {
                  data.onChange(data.id, { ai_mode_enabled: true });
                }
              }}
              color="primary"
            />
          }
          label="AI"
          labelPlacement="end"
        />
        {data.ai_mode_enabled && (
          <SearchableDropdown
            options={parentResults.map((item) => ({
              label: item.label,
              value: item.value,
            }))}
            multiSelect={true}
            onChange={(selectedOptions) => {
              const selectedValues = (selectedOptions as any[]).map((opt) => opt.value);
              data.onChange(data.id, { ai_query_parameters: selectedValues });
            }}
            value={parentResults
              .map((item) => ({ label: item.label, value: item.value }))
              .filter((item) => (data.ai_query_parameters || []).includes(item.value))}
          />
        )}
      </Box>
      {/* Header Input Field */}
      <div style={{ marginBottom: '10px', position: 'relative' }}>
        <label>Header:</label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={data.header || ''}
            onChange={(e) => {
              const newValue = e.target.value;
              if (newValue.length <= 60) {
                data.onChange(data.id, { header: newValue });
              }
            }}
            placeholder="Enter Header Text"
            style={{
              width: '100%',
              padding: '5px',
              paddingRight: '50px', // Reserve space for character count
              border: '1px solid #ccc',
              borderRadius: '4px',
              background: '#f5f5f5',
            }}
          />
          <span
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '12px',
              color: data.header?.length === 60 ? 'red' : '#888',
            }}
          >
            {`${(data.header || '').length}/60`}
          </span>
        </div>
        {flowValidationObj[data?.id]?.header && (
          <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
            {flowValidationObj[data?.id]?.header}
          </Typography>
        )}
      </div>

      {/* Body Input Field */}
      <RenderTextArea data={data} />
      {flowValidationObj[data?.id]?.body && (
        <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
          {flowValidationObj[data?.id]?.body}
        </Typography>
      )}

      {/* {showSuggestions && data?.parentResults?.length > 0 && (
        <div
          style={{
            position: 'absolute',
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: 4,
            zIndex: 1000,
            width: '100%',
          }}
        >
          {data?.parentResults?.map((suggestion: SuggestionObj, index: number) => (
            <div
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              style={{
                padding: '5px 10px',
                cursor: 'pointer',
                backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff',
              }}
            >
              {suggestion.label}
            </div>
          ))}
        </div>
      )} */}
      {/* Footer Input Field */}
      <div style={{ marginBottom: '10px', position: 'relative' }}>
        <label>Footer:</label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={data.footer || ''}
            onChange={(e) => {
              const newValue = e.target.value;
              if (newValue.length <= 60) {
                data.onChange(data.id, { footer: newValue });
              }
            }}
            placeholder="Enter Footer Text"
            style={{
              width: '100%',
              padding: '5px',
              paddingRight: '50px', // Reserve space for character count
              border: '1px solid #ccc',
              borderRadius: '4px',
              background: '#f5f5f5',
            }}
          />
          <span
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '12px',
              color: data.footer?.length === 60 ? 'red' : '#888',
            }}
          >
            {`${(data.footer || '').length}/60`}
          </span>
        </div>
        {flowValidationObj[data?.id]?.footer && (
          <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
            {flowValidationObj[data?.id]?.footer}
          </Typography>
        )}
      </div>
      <FormControlLabel
        control={
          <Switch
            checked={data.headerTypeIsUrl || false}
            onChange={() =>
              data.onChange(data.id, {
                headerTypeIsUrl: !data.headerTypeIsUrl,
                media: {},
              })
            }
          />
        }
        label={`Image url`}
        labelPlacement="start"
      />

      {/* Image Node */}
      {data.type === 'image' && (
        <div>
          {data.headerTypeIsUrl ? (
            <>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Paste image url"
                value={data?.media?.s3_object?.url || ''}
                onChange={(e) => {
                  const url = e.target.value;
                  const fileName = url?.split('/')?.pop() || '';
                  data.onChange(data.id, {
                    media: {
                      ...(data.media || {}),
                      url: url,
                      filename: fileName,
                      s3_object: {
                        ...(data.media?.s3_object || {}),
                        url: url,
                        filename: fileName,
                      },
                    },
                  });
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={async () => {
                          try {
                            const text = await navigator.clipboard.readText();
                            const fileName = text?.split('/')?.pop() || '';
                            data.onChange(data.id, {
                              media: {
                                ...(data.media || {}),
                                url: text,
                                filename: fileName,
                                s3_object: {
                                  ...(data.media?.s3_object || {}),
                                  url: text,
                                  filename: fileName,
                                },
                              },
                            });
                          } catch (err) {
                            console.error('Failed to read clipboard:', err);
                          }
                        }}
                        edge="end"
                        aria-label="paste"
                      >
                        <ContentPasteIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                placeholder="Paste image url"
              />
              {data?.media?.s3_object?.url && (
                <div
                  style={{
                    position: 'relative', // Ensures the CloseIcon is positioned relative to this container
                    width: '100%',
                    borderRadius: '4px',
                    marginTop: '39px',
                  }}
                >
                  <CloseIcon
                    style={{
                      position: 'absolute',
                      top: '-31px',
                      right: '5px',
                      color: 'red',
                      cursor: 'pointer',
                      fontSize: '18px',
                    }}
                    onClick={() =>
                      data.onChange(data.id, {
                        media: {},
                      })
                    }
                  />
                  <img
                    src={data.media.s3_object.url}
                    alt="Preview"
                    style={{ width: '100%', borderRadius: '4px' }}
                  />
                </div>
              )}
            </>
          ) : data?.media?.s3_object?.url ? (
            <div style={{ position: 'relative', marginTop: '15px' }}>
              <CloseIcon
                style={{
                  position: 'absolute',
                  top: '-41px',
                  right: '0px',
                  color: 'red',
                  cursor: 'pointer',
                  fontSize: '18px',
                }}
                onClick={() =>
                  data.onChange(data.id, {
                    media: {},
                  })
                }
              />
              <img
                src={data.media.s3_object.url}
                alt="Uploaded"
                style={{ width: '100%', borderRadius: '4px' }}
              />
            </div>
          ) : (
            <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
              {isLoading ? (
                <LoadingWrapper />
              ) : (
                <>
                  {/* <label>Upload Image:</label>
                  <input
                    type="file"
                    accept="image/jpeg, image/png, image/gif, image/webp"
                    onChange={handleImageUpload}
                    style={{ width: '100%', marginBottom: '10px' , }}
                  /> */}
                  <Typography variant="subtitle1" gutterBottom>
                    Upload Image
                  </Typography>
                  <FileInput
                    onFileSelect={handleImageUpload}
                    // onFileRemove={onHeaderFileRemove}
                    // file={headerFile}
                    // setFile={setHeaderFile}
                    accept="image/jpeg,image/png"
                  />
                  <FormHelperText sx={{ mt: 1 }}>Allowed: JPG, PNG • Max 5 MB</FormHelperText>
                </>
              )}
            </div>
          )}
          {flowValidationObj[data?.id]?.url && (
            <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
              {flowValidationObj[data?.id]?.url}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageNode;
